/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        // Fix 2: Null checks before DOM access (HIGH)
        this.section = this.closest('.section-main-product');
        if (!this.section) {
          console.error('StickyAtc: Critical element .section-main-product not found');
          return;
        }

        this.buyButtons = this.section.querySelector('.quantity-submit-row');
        if (!this.buyButtons) return;

        this.mainProductForm = this.section.querySelector('product-form');
        this.mainProductSubmitForm = this.mainProductForm.getSubmitForm();
        this.submitForm = this.getSubmitForm();
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));
        this.button = this.querySelector('.add-to-cart');
        
        // Fix 2: Check that button exists before proceeding
        if (!this.button) {
          console.error('StickyAtc: Critical element .add-to-cart button not found');
          return;
        }

        // Fix 5: Cache redundant DOM queries (MEDIUM)
        this.variantInput = this.section.querySelector('.js-product-form input[name="id"]');
        this.productInfo = this.section.querySelector('#product-info');

        this.throttledOnScroll = throttle(this.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);

        // Fix 1: Store bound handler as named reference for cleanup
        this.boundUpdateImage = this.updateImage.bind(this);
        this.section.addEventListener('on:media-gallery:change', this.boundUpdateImage);

        this.updateImage();
        this.updateAddToCartButton();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
        
        // Fix 1: Event listener leak cleanup for 'on:media-gallery:change' listener
        if (this.section && this.boundUpdateImage) {
          this.section.removeEventListener('on:media-gallery:change', this.boundUpdateImage);
        }
      }

      /**
       * Helper method for safe scrolling with fallback.
       * Fix 4: Add defensive check for theme.scrollToRevealElement (MEDIUM)
       */
      _scrollTo(el) {
        if (!el) return;
        if (typeof theme !== 'undefined' && typeof theme.scrollToRevealElement === 'function') {
          theme.scrollToRevealElement(el);
        } else {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        // Fix 6: Add try/catch to async handleSubmit (MEDIUM)
        try {
          evt.preventDefault();

          // Fix 5: Use cached variantInput instead of inline query
          const variantId = this.variantInput?.value;
          if (!variantId) {
            // Fix 5: Use cached productInfo instead of inline query
            // Fix 4: Use _scrollTo helper instead of direct theme.scrollToRevealElement
            if (this.productInfo && this.productInfo.offsetParent) {
              evt.preventDefault();
              this._scrollTo(this.productInfo);
            }
            return;
          }

          // Validate main form
          const customFormValid = this.mainProductForm.validate();
          if (!customFormValid || !this.mainProductSubmitForm.reportValidity()) {
            evt.preventDefault();
            if (!customFormValid) {
              // Fix 4: Use _scrollTo helper instead of direct theme.scrollToRevealElement
              this._scrollTo(this.mainProductForm);
            } else {
              const input = Array.from(this.mainProductSubmitForm.elements).find(
                (el) => !el.checkValidity()
              );
              // Fix 4: Use _scrollTo helper instead of direct theme.scrollToRevealElement
              setTimeout(() => this._scrollTo(input), 100);
            }
            return;
          }

          // Clear copied data
          this.submitForm.querySelectorAll('[data-copied]').forEach((el) => el.remove());

          // Copy data
          const formData = new FormData(this.mainProductSubmitForm);
          for (const p of formData) {
            if (!this.submitForm.querySelector(`[name="${p[0]}"]`)) {
              const input = document.createElement('input');
              input.name = p[0];
              input.value = p[1];
              input.hidden = true;
              input.setAttribute('data-copied', '');
              this.submitForm.append(input);
            }
          }
          super.handleSubmit(evt);
        } catch (error) {
          console.error('StickyAtc.handleSubmit error:', error);
          evt.preventDefault();
        }
      }

      /**
       * Determine visibility after scroll.
       */
      handleScroll() {
        const topOffset = document.querySelector('.pageheader--sticky') ? document.querySelector('.section-header').offsetHeight : 0;
        this.classList.toggle(
          'sticky-atc--out',
          this.buyButtons.getBoundingClientRect().bottom > topOffset
        );

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Updates the image.
       */
      updateImage() {
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          // Fix 3: Replace innerHTML with cloneNode (HIGH)
          this.imageContainer.innerHTML = '';
          this.imageContainer.appendChild(mainImage.cloneNode(true));
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Updates the add to cart button text.
       */
      updateAddToCartButton() {
        // Fix 5: Use cached variantInput instead of inline query
        const variantId = this.variantInput?.value;
        if (!variantId) {
          this.button.innerHTML = this.button.dataset.unavailableText;
        }
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
